/* ──────────────────────────────────────────────────────────────────────────
 * Tax math — estimated Canadian marginal tax rate and interest-deductibility
 * savings for the simulator's results section.
 *
 * **Scope and honesty:** the simulator gives an ESTIMATE of how much a user
 * could save on tax through investment-loan interest deductibility. Total
 * accuracy requires a full tax return (other income sources, capital gains
 * realized, dividend tax credits, RRSP / charitable deductions, carryovers,
 * etc.). For the simulator's purpose — "is this strategy worth it?" — an
 * estimate within a couple percentage points is enough.
 *
 * **Bracket data:** the federal + provincial bracket schedules below are
 * sourced from each jurisdiction's most-recently-published public tax
 * schedules. They need YEARLY MAINTENANCE — the founder updates them when
 * Canada Revenue Agency and provincial finance ministries publish the new
 * year's brackets (usually announced in November-December for the following
 * tax year, with inflation indexing).
 *
 * Currently the data here uses **2024 published brackets** as the
 * approximation for the current tax year — bracket thresholds are
 * inflation-indexed yearly so the exact numbers shift by a few percent
 * annually, but the strategic estimate they produce is robust.
 *
 * Per docs/FINANCE_RULES.md and the no-assumed-values doctrine: bracket
 * values below are real published numbers, NOT invented constants. Sources:
 *   - Federal: https://www.canada.ca/en/revenue-agency/services/tax/individuals/frequently-asked-questions-individuals/canadian-income-tax-rates-individuals-current-previous-years.html
 *   - BC: https://www2.gov.bc.ca/gov/content/taxes/income-taxes/personal/tax-rates
 *
 * **Simplifications documented in MATH.md:**
 *   - Today's brackets used for all historical years (income and brackets
 *     both inflation-adjust, mostly cancelling out for the simulator's
 *     "would this strategy have worked?" question).
 *   - Interest deduction is treated as `interest × marginal_rate`. Doesn't
 *     model deduction-spans-multiple-brackets (interest is usually small
 *     enough to stay within one bracket for realistic scenarios).
 *   - Doesn't model: dividend tax credit, eligible-vs-non-eligible dividend
 *     grossup, capital gains tax, foreign withholding, Quebec's parallel
 *     federal system, multi-year carryover.
 *   - Only British Columbia provincial brackets are implemented as of Slice
 *     8. Other provinces appear in the dropdown but the calculation falls
 *     back to BC with a UI disclosure.
 * ────────────────────────────────────────────────────────────────────────── */

// ── Jurisdiction catalog ──────────────────────────────────────────────────

/** All 10 provinces + 3 territories. Stable codes. */
export type Jurisdiction =
  | "BC"
  | "AB"
  | "SK"
  | "MB"
  | "ON"
  | "QC"
  | "NB"
  | "NS"
  | "PE"
  | "NL"
  | "YT"
  | "NT"
  | "NU";

export type JurisdictionMeta = {
  /** Two-letter code, the canonical identifier. */
  readonly code: Jurisdiction;
  /** Full display name. */
  readonly name: string;
  /**
   * True when this jurisdiction has its own implemented bracket data. When
   * false, the tax calculation falls back to BC and the UI shows a
   * "coming soon, estimated using BC rates" disclosure.
   */
  readonly supported: boolean;
};

export const JURISDICTIONS: Readonly<Record<Jurisdiction, JurisdictionMeta>> = {
  BC: { code: "BC", name: "British Columbia", supported: true },
  AB: { code: "AB", name: "Alberta", supported: false },
  SK: { code: "SK", name: "Saskatchewan", supported: false },
  MB: { code: "MB", name: "Manitoba", supported: false },
  ON: { code: "ON", name: "Ontario", supported: false },
  QC: { code: "QC", name: "Quebec", supported: false },
  NB: { code: "NB", name: "New Brunswick", supported: false },
  NS: { code: "NS", name: "Nova Scotia", supported: false },
  PE: { code: "PE", name: "Prince Edward Island", supported: false },
  NL: { code: "NL", name: "Newfoundland and Labrador", supported: false },
  YT: { code: "YT", name: "Yukon", supported: false },
  NT: { code: "NT", name: "Northwest Territories", supported: false },
  NU: { code: "NU", name: "Nunavut", supported: false },
};

/** Alphabetical list of jurisdictions for dropdown rendering. */
export const JURISDICTION_LIST: readonly JurisdictionMeta[] = Object.values(JURISDICTIONS)
  .slice()
  .sort((a, b) => a.name.localeCompare(b.name));

// ── Bracket schedules ────────────────────────────────────────────────────

/**
 * One bracket entry. The rate applies to the NEXT dollar of income earned
 * at any income level below `upTo`. The last bracket in each schedule uses
 * `Infinity` to represent the top rate.
 */
type Bracket = {
  readonly upTo: number;
  readonly rate: number;
};

/**
 * Federal brackets — 2024 published schedule (Canada Revenue Agency).
 * Update annually with each year's inflation-indexed thresholds.
 */
const FEDERAL_BRACKETS: readonly Bracket[] = [
  { upTo: 55_867, rate: 0.15 },
  { upTo: 111_733, rate: 0.205 },
  { upTo: 173_205, rate: 0.26 },
  { upTo: 246_752, rate: 0.29 },
  { upTo: Infinity, rate: 0.33 },
];

/**
 * BC provincial brackets — 2024 published schedule (BC Ministry of Finance).
 * Update annually with each year's inflation-indexed thresholds.
 */
const BC_BRACKETS: readonly Bracket[] = [
  { upTo: 47_937, rate: 0.0506 },
  { upTo: 95_875, rate: 0.077 },
  { upTo: 110_076, rate: 0.105 },
  { upTo: 133_664, rate: 0.1229 },
  { upTo: 181_232, rate: 0.147 },
  { upTo: 252_752, rate: 0.168 },
  { upTo: Infinity, rate: 0.205 },
];

const PROVINCIAL_BRACKETS_BY_JURISDICTION: Partial<Record<Jurisdiction, readonly Bracket[]>> = {
  BC: BC_BRACKETS,
  // Other provinces fall back to BC (see notes above).
};

// ── Public functions ──────────────────────────────────────────────────────

/**
 * Find the marginal tax rate for the NEXT dollar of income earned at the
 * given income level. Looks up the bracket where `income < bracket.upTo`.
 */
function marginalRateInSchedule(brackets: readonly Bracket[], income: number): number {
  for (const b of brackets) {
    if (income < b.upTo) return b.rate;
  }
  // Defensive — last bracket uses Infinity so we shouldn't reach here.
  return brackets[brackets.length - 1]!.rate;
}

/**
 * Estimated combined (federal + provincial) marginal tax rate for the next
 * dollar of income earned, given gross household income and jurisdiction.
 *
 * Returns a decimal (e.g., 0.4329 for ~43.3% combined). For non-BC
 * jurisdictions, currently falls back to BC's provincial schedule — the
 * caller is responsible for surfacing the disclosure in the UI.
 */
export function estimatedCombinedMarginalRate(
  income: number,
  jurisdiction: Jurisdiction = "BC",
): number {
  if (!Number.isFinite(income) || income < 0) {
    return 0;
  }
  const provincialBrackets = PROVINCIAL_BRACKETS_BY_JURISDICTION[jurisdiction] ?? BC_BRACKETS;
  const federalRate = marginalRateInSchedule(FEDERAL_BRACKETS, income);
  const provincialRate = marginalRateInSchedule(provincialBrackets, income);
  return federalRate + provincialRate;
}

/**
 * Estimated dollar value of tax savings from interest deductibility, given
 * the total interest paid and the marginal tax rate.
 *
 * This is the simple `interest × rate` calculation. Doesn't model:
 *   - Deduction pushing the user down a bracket (irrelevant for realistic
 *     interest amounts — interest is usually small enough to stay in one
 *     bracket).
 *   - Year-by-year application (we assume the user has enough income each
 *     year to use the full deduction).
 *   - Carryforward of unused deductions.
 */
export function estimatedDeductibilitySavings(
  cumulativeInterestPaid: number,
  marginalRate: number,
): number {
  if (
    !Number.isFinite(cumulativeInterestPaid) ||
    !Number.isFinite(marginalRate) ||
    cumulativeInterestPaid <= 0 ||
    marginalRate <= 0
  ) {
    return 0;
  }
  return cumulativeInterestPaid * marginalRate;
}
